import {
  Component,
  ChangeDetectionStrategy,
  input,
  output,
  signal,
  computed,
  viewChild,
  ElementRef,
  afterRenderEffect,
  inject
} from '@angular/core';
import { NgIcon, provideIcons } from '@ng-icons/core';
import {
  iconoirMicrophone,
  iconoirSendDiagonal,
  iconoirWarningTriangle,
  iconoirSquare,
  iconoirPlus,
  iconoirFolderPlus,
  iconoirUserPlus,
  iconoirCalendarPlus,
  iconoirRefreshDouble,
  iconoirTrash,
  iconoirTaskList,
  iconoirHouseRooms,
  iconoirGraphUp,
  iconoirChatBubble,
  iconoirWarningCircle,
  iconoirArrowUpRight
} from '@ng-icons/iconoir';
import { VoiceChatMessage, PendingFollowUp, VoiceMessageOption, VoiceMessageLink } from '../../../models';
import { QUICK_ACTIONS, QuickActionFlow, QuickActionDef, CONTEXTUAL_QUICK_ACTIONS } from '../../../constants';
import { VoiceContextService } from '../../../services/voice-context.service';
import { VoiceBriefingService } from '../../../services/voice-briefing.service';
import { AuthService } from '../../../../auth/auth.service';
import { VoiceAssistantAvatarComponent } from './voice-assistant-avatar/voice-assistant-avatar.component';
import { KoffiState, VoiceRobotComponent } from './voice-robot/voice-robot.component';
import { SlashCommandPaletteComponent } from './slash-command-palette/slash-command-palette.component';

/** Distance (px) au fond du fil en-dessous de laquelle on considère l'utilisateur "en bas" (tolérance scroll standard) */
const NEAR_BOTTOM_THRESHOLD_PX = 150;

/**
 * Fil de conversation de l'assistant vocal
 * Affiche les messages user/assistant, la zone de saisie texte et le déclencheur micro.
 * Absorbe aussi les états transitoires (traitement, transcription en cours, erreur) que
 * gérait auparavant `VoiceFeedbackComponent`.
 *
 * MOKA-206 Phase 3 : composant purement présentatif pour les flows guidés —
 * il détecte l'ouverture de la palette « / » et les clics (chips d'accueil,
 * palette, boutons de réponse rapide) mais ne connaît RIEN de
 * `VoiceGuidedFlowService`/`VoiceClarificationService` : tout part en
 * `output()` vers `VoiceAssistantOverlayComponent`, qui seul orchestre l'état
 * (cf. pattern déjà en place pour `sendText`/`startVoice`).
 */
@Component({
  selector: 'app-voice-chat-thread',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgIcon, SlashCommandPaletteComponent, VoiceAssistantAvatarComponent, VoiceRobotComponent],
  viewProviders: [
    provideIcons({
      iconoirMicrophone,
      iconoirSendDiagonal,
      iconoirWarningTriangle,
      // Icône "stop" pendant l'enregistrement (Lot E, addendum toggle micro) —
      // simple carré plein, glyphe universel de stop (lecteurs média...).
      iconoirSquare,
      // Icônes des chips d'actions rapides (cf. QUICK_ACTIONS) — rendues dans
      // CE template via `[name]="action.icon"`, donc enregistrées ICI et pas
      // seulement dans SlashCommandPaletteComponent (contextes d'injection
      // distincts : le fournisseur de l'un ne couvre pas l'autre).
      iconoirPlus,
      iconoirFolderPlus,
      iconoirUserPlus,
      iconoirCalendarPlus,
      iconoirRefreshDouble,
      iconoirTrash,
      // Ajoutées MOKA-207 Phase 4 Lot 4 (cf. QUICK_ACTIONS étendu) —
      // iconoirTaskList/iconoirHouseRooms couvrent les 2 nouvelles chips
      // primary (myTasks/reserveRoom), les 3 autres restent secondaires
      // (rendues seulement par SlashCommandPaletteComponent) mais sont
      // enregistrées ici aussi par cohérence avec le reste du fichier.
      iconoirTaskList,
      iconoirHouseRooms,
      iconoirGraphUp,
      iconoirChatBubble,
      iconoirWarningCircle,
      // Chips de navigation vers les entités citées (MOKA-208 P2 #1)
      iconoirArrowUpRight
    })
  ],
  templateUrl: './voice-chat-thread.component.html',
  styleUrl: './voice-chat-thread.component.scss'
})
export class VoiceChatThreadComponent {
  private voiceContext = inject(VoiceContextService);
  private briefingService = inject(VoiceBriefingService);
  private auth = inject(AuthService);

  /**
   * Salutation de Koffi. Le prénom quand on le connaît : « Bonjour William »
   * vaut mieux qu'une formule générique, et son absence ne doit pas laisser
   * une virgule orpheline.
   */
  protected greeting = computed(() => {
    const firstName = this.auth.profile?.['first_name'] as string | undefined;
    return firstName
      ? `Bonjour ${firstName}, comment puis-je vous aider aujourd'hui ?`
      : `Bonjour, comment puis-je vous aider aujourd'hui ?`;
  });

  /** Accroche d'ouverture (tâches en retard) — cf. VoiceBriefingService. */
  protected briefing = this.briefingService.briefing;

  messages = input<VoiceChatMessage[]>([]);
  pendingFollowUp = input<PendingFollowUp | null>(null);
  /** Identifiant de la carte de confirmation encore active (MOKA-299) ; les autres n'ont plus de boutons. */
  activeConfirmationId = input<string | null>(null);
  isProcessing = input(false);
  isRecording = input(false);
  /**
   * Lecture TTS en cours (Lot E, retour terrain) : depuis la suppression du
   * panneau orbe, c'est `.chat-thread__mic-btn` qui porte l'indicateur visuel
   * de parole — halo bleu distinct de la pulsation rouge d'enregistrement,
   * jamais les deux en même temps (`isRecording`/`isSpeaking` s'excluent déjà
   * mutuellement côté `VoiceService`, cf. `VoiceState.status`).
   */
  isSpeaking = input(false);
  liveTranscript = input('');
  hasError = input(false);
  error = input<string | null>(null);
  /** Une demande écrite a échoué et peut être renvoyée telle quelle (MOKA-363). */
  canRetry = input(false);
  retry = output<void>();

  /**
   * Pose de Koffi sur l'écran d'accueil : on peut dicter avant le premier
   * message, la mascotte doit alors montrer qu'elle écoute. Enregistrement et
   * lecture s'excluent déjà côté `VoiceService` ; l'ordre ne sert qu'à trancher
   * un état transitoire.
   */
  protected koffiState = computed<KoffiState>(() => {
    if (this.isRecording()) return 'ecoute';
    if (this.isSpeaking()) return 'parle';
    if (this.isProcessing()) return 'reflexion';
    return 'repos';
  });

  sendText = output<string>();
  startVoice = output<void>();
  /**
   * Arrêt manuel de l'enregistrement (Lot E, addendum) : émis par
   * `onMicClick()` quand `isRecording()` est vrai, plutôt que de réutiliser
   * `startVoice` (sémantique différente — évite toute ambiguïté côté parent).
   */
  stopVoice = output<void>();
  /** Action rapide choisie (chip d'accueil, ou palette « / ») */
  quickAction = output<QuickActionFlow>();
  /** Bouton de réponse rapide cliqué sous une bulle assistant (cf. modèle `VoiceChatMessage.options`) */
  optionSelected = output<VoiceMessageOption>();
  /** Navigation vers une entité citée dans une réponse (MOKA-208 P2 #1). */
  linkSelected = output<VoiceMessageLink>();

  draft = signal('');

  /** Les 4 actions mises en avant (cf. `QuickActionDef.primary`), affichées sous le message d'accueil */
  /**
   * Actions proposées à l'ouverture. Adaptées à la page consultée (MOKA-208 P4)
   * plutôt qu'au catalogue générique : sur un projet, « Réserver une salle »
   * n'est qu'une ligne de plus à écarter. Hors module connu, on retombe sur les
   * actions `primary` du catalogue.
   */
  primaryActions = computed<QuickActionDef[]>(() => {
    const module = this.voiceContext.activeModule();
    const ids = module ? CONTEXTUAL_QUICK_ACTIONS[module] : undefined;
    if (!ids?.length) return QUICK_ACTIONS.filter(action => action.primary);

    return ids
      .map(id => QUICK_ACTIONS.find(action => action.id === id))
      .filter((action): action is QuickActionDef => !!action);
  });

  /** Palette « / » ouverte dès que le brouillon commence par `/` */
  slashPaletteOpen = computed(() => this.draft().startsWith('/'));

  /**
   * Sélection clavier courante dans la palette. Reste ici (pas dans
   * `SlashCommandPaletteComponent`) — voir la JSDoc de ce dernier pour le
   * raisonnement complet : centraliser le clavier là où le focus DOM vit
   * réellement (le textarea) évite tout va-et-vient de focus.
   */
  paletteActiveIndex = signal(0);

  /** Actions filtrées par le texte tapé après le `/` — dupliqué ici (même filtre que la palette) pour borner la navigation clavier */
  paletteActions = computed(() => {
    const term = this.draft().slice(1).toLowerCase();
    return QUICK_ACTIONS.filter(action => action.label.toLowerCase().includes(term));
  });

  private inputRef = viewChild<ElementRef<HTMLTextAreaElement>>('draftInput');
  private messagesContainer = viewChild<ElementRef<HTMLDivElement>>('messagesContainer');

  /**
   * `true` tant que l'utilisateur n'a pas remonté volontairement dans
   * l'historique (mis à jour EN CONTINU par un listener `scroll` natif posé
   * une fois dans le constructeur — pas un `(scroll)` de template : ce lot se
   * limite au strict TS, le SCSS/layout de ce fichier est retravaillé en
   * parallèle par un autre agent). Se base sur la position AVANT l'arrivée
   * d'un nouveau message, ce qu'une mesure DOM faite APRÈS coup (dans l'effet
   * de scroll ci-dessous) ne peut plus distinguer une fois le nouveau message
   * déjà rendu.
   */
  #stickToBottom = true;
  #scrollListenerAttached = false;
  #previousMessageCount = 0;
  /** `false` jusqu'au tout premier scroll effectué — ce premier scroll (fil déjà non vide au montage : reprise de session, réouverture de l'overlay) est toujours instantané, sans passer par le test "était en bas ?" */
  #hasScrolledOnce = false;

  constructor() {
    /**
     * Auto-scroll du fil (retour terrain) :
     * - message `user` ajouté (texte, vocal, clic bouton) → toujours en bas ;
     * - message `assistant` ajouté → en bas SEULEMENT si `#stickToBottom` ;
     * - tout premier rendu avec des messages déjà présents (reprise de
     *   session, réouverture de l'overlay) → en bas instantanément.
     * `afterRenderEffect` (plutôt qu'un `setTimeout` arbitraire) garantit que
     * la lecture/écriture du scroll intervient APRÈS que ce rendu — y compris
     * les boutons de réponse rapide sous certains messages assistant, qui
     * changent la hauteur du contenu — soit posé dans le DOM.
     */
    afterRenderEffect(() => {
      const container = this.messagesContainer()?.nativeElement;
      if (!container) return;

      if (!this.#scrollListenerAttached) {
        this.#scrollListenerAttached = true;
        container.addEventListener('scroll', () => {
          const distanceFromBottom = container.scrollHeight - container.scrollTop - container.clientHeight;
          this.#stickToBottom = distanceFromBottom < NEAR_BOTTOM_THRESHOLD_PX;
        }, { passive: true });
      }

      const msgs = this.messages();
      const added = msgs.length > this.#previousMessageCount;
      this.#previousMessageCount = msgs.length;
      if (!added) return;

      const lastMessage = msgs[msgs.length - 1];
      const shouldScroll = !this.#hasScrolledOnce || lastMessage?.role === 'user' || this.#stickToBottom;

      if (shouldScroll) {
        container.scrollTop = container.scrollHeight;
        this.#stickToBottom = true;
        this.#hasScrolledOnce = true;
      }
    });
  }

  /** Focus dans le champ de saisie : ouverture du panneau, micro refusé (MOKA-363). */
  focusInput(): void {
    this.inputRef()?.nativeElement.focus();
  }

  onInput(event: Event): void {
    this.draft.set((event.target as HTMLTextAreaElement).value);
    this.paletteActiveIndex.set(0);
  }

  onSubmit(): void {
    const value = this.draft().trim();
    if (!value) return;
    this.sendText.emit(value);
    this.draft.set('');
    this.inputRef()?.nativeElement.focus();
  }

  /**
   * Quand la palette est ouverte, ↑/↓/Entrée/Échap sont interceptées AVANT le
   * comportement par défaut du textarea (navigation/soumission) ; toute autre
   * touche continue normalement de modifier `draft` (filtre de la palette).
   * Sinon, comportement historique inchangé (Entrée sans Shift = envoyer).
   */
  onKeydown(event: KeyboardEvent): void {
    if (this.slashPaletteOpen()) {
      const count = this.paletteActions().length;

      if (event.key === 'ArrowDown') {
        event.preventDefault();
        if (count > 0) {
          this.paletteActiveIndex.update(i => (i + 1) % count);
        }
        return;
      }

      if (event.key === 'ArrowUp') {
        event.preventDefault();
        if (count > 0) {
          this.paletteActiveIndex.update(i => (i - 1 + count) % count);
        }
        return;
      }

      if (event.key === 'Enter') {
        event.preventDefault();
        const action = this.paletteActions()[this.paletteActiveIndex()];
        if (action) {
          this.onQuickAction(action.id);
        }
        return;
      }

      if (event.key === 'Escape') {
        event.preventDefault();
        this.closeSlashPalette();
        return;
      }

      return;
    }

    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.onSubmit();
    }
  }

  /**
   * Toggle start/stop (Lot E, addendum) : un enregistrement en cours est
   * arrêté manuellement (`stopVoice`) — l'arrêt naturel (silence détecté,
   * durée max) et l'arrêt manuel convergent déjà tous les deux vers le même
   * `onstop`/`resolve` côté `AudioCaptureService`, rien à dupliquer ici.
   */
  onMicClick(): void {
    if (this.isRecording()) {
      this.stopVoice.emit();
    } else {
      this.startVoice.emit();
    }
  }

  onQuickAction(flow: QuickActionFlow): void {
    this.draft.set('');
    this.paletteActiveIndex.set(0);
    this.quickAction.emit(flow);
  }

  closeSlashPalette(): void {
    this.draft.set('');
    this.paletteActiveIndex.set(0);
  }

  /**
   * `true` si ce message ouvre une prise de parole de Koffi (premier message,
   * ou précédé d'un message de l'utilisateur). Sert à n'afficher l'avatar et
   * l'en-tête « Koffi · heure » qu'une fois par tour de parole.
   */
  protected startsAssistantTurn(index: number): boolean {
    const list = this.messages();
    if (list[index]?.role !== 'assistant') return false;
    return index === 0 || list[index - 1]?.role !== 'assistant';
  }

  /** Heure courte (14:05), comme dans une messagerie. */
  protected formatTime(timestamp: number): string {
    return new Date(timestamp).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  }

  onLinkClick(link: VoiceMessageLink): void {
    this.linkSelected.emit(link);
  }

  onOptionClick(option: VoiceMessageOption): void {
    this.optionSelected.emit(option);
  }
}
